import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { FiChevronRight, FiPlus } from "react-icons/fi";
import { format, isSameDay } from "date-fns";
import { useTranslation } from "react-i18next";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { Button } from "./Button";
import { BookingSearch, type BookingSearchLabels } from "./BookingSearch";
import { Tooltip } from "./Tooltip";
import { useCalendarLabels } from "./useCalendarLabels";
import { useGuestSelectLabels } from "./useGuestSelectLabels";
import {
  createUnitId,
  SearchUnit,
  useHeader,
} from "../contexts/HeaderContext";
import { useConfiguration } from "../contexts/ConfigurationProvider";
import { Guest } from "../api/contracts/category-ads";
import { AgeType } from "../api/contracts/reservation";
import { formatLocalDate } from "../contexts/HeaderContext";
import { resizeGuestList } from "../utils/resizeGuestList";
import { keepActiveUnit, sumGuestTypesAcrossUnits } from "../utils/guestUnitTotals";
import { isInfantsOnlyGuestList } from "../utils/guestCombination";
import {
  getStopTimeMessageForStayRange,
  isStayRangeBlockedByStopTime,
} from "../utils/bookingAvailabilityUtils";
import { getDateFnsLocale, getLanguageCode } from "../utils/language";
import { toasty } from "./Toast";
import { useBookingCodeStore } from "../stores/bookingCodeStore";
import { BookingCodeSearchSegment } from "./BookingCodeSearchSegment";
import { useBookingCodeCommit } from "./useBookingCodeCommit";
import { useAdFormat } from "../hooks/useAdFormat";
import { bookedUnitIds, useBookingFlowStore, unitCapReached } from "../booking-flow/bookingFlowStore";

/**
 * Standalone desktop search pill. The shared shell (dates pill, guests pill,
 * calendar/guest popovers, click-outside) lives in <BookingSearch /> so the
 * embed widget renders the exact same UI. This wrapper layers app-only
 * concerns on top: HeaderContext wiring, multi-unit tabs inside the guests
 * popover, and stop-time toast on submit.
 *
 * Edits made inside the popover (dates, guest counts, units) are staged in
 * local draft state and only flushed to HeaderContext when the user clicks
 * the search icon or "Uppdatera". That keeps the URL and search results
 * stable while the user is editing — they only update on explicit commit.
 * Closing the popover without committing KEEPS the drafts staged in the pill
 * (picking dates, hopping to guests, then clicking outside must not wipe the
 * picked dates); drafts only re-seed when the committed context itself
 * changes (search commit, mobile-sheet commit, URL restore).
 */

export type HeaderSearchBarProps = {
  onSearch?: () => void;
};

export const HeaderSearchBar: React.FC<HeaderSearchBarProps> = ({
  onSearch,
}) => {
  const { t } = useTranslation();
  const calendarLocalization = useCalendarLabels();
  const guestSelectLabels = useGuestSelectLabels();
  const {
    currentMonth,
    selectedStartDate,
    selectedEndDate,
    units,
    activeUnitId,
    bookingAvailability,
    bookingAvailabilityFetching,
    openCalendarRequest,
    setOpenCalendarRequest,
    setCurrentMonth,
    setSelectedStartDate,
    setSelectedEndDate,
    replaceUnits,
    setActiveUnitId,
    getBedTypeForAge,
    serializeSearchParams,
  } = useHeader();
  const { configuration: wbeConfig } = useConfiguration();
  const { isActivity } = useAdFormat();

  // Draft state — popover edits write here, not to context. Committed in
  // commitAndSearch(); kept staged on close-without-commit.
  const [draftStartDate, setDraftStartDate] = useState<Date | null>(
    selectedStartDate,
  );
  const [draftEndDate, setDraftEndDate] = useState<Date | null>(selectedEndDate);
  // The booking flow owns the plan's units. A unit whose rows are in the cart is a booking, not
  // a search draft — it is switched and dropped in the flow, never here. This bar then edits
  // the units still to book: add, remove, change their parties. A search puts those back
  // beside the booked ones untouched; replacing the whole list from the draft used to drop
  // every booked unit out of the plan while its rows stayed in the cart, orphaned.
  const plan = useBookingFlowStore((s) => s.plan);
  const booked = useMemo(() => bookedUnitIds(plan), [plan]);
  const draftFor = useCallback(
    (all: SearchUnit[], active: string): SearchUnit[] => {
      const single = (list: SearchUnit[]) =>
        isActivity ? keepActiveUnit(list, active) : list;
      const open = all.filter((u) => !booked.has(u.id));
      if (open.length > 0) return single(open);
      // Everything booked: the next search is for a new unit, seeded the way `addUnit` seeds one.
      const seed = all.find((u) => u.id === active) ?? all[0];
      return [
        {
          id: createUnitId(),
          guests: seed
            ? seed.guests.map((g) => ({ ...g }))
            : [{ ageType: "adult", bedCapacityType: "full" } as Guest],
        },
      ];
    },
    [booked, isActivity],
  );
  // The header's active unit may be a booked one, absent from the draft — then the first
  // unit still to book is the one open in the card.
  const activeIn = (draft: SearchUnit[], active: string): string =>
    draft.some((u) => u.id === active) ? active : (draft[0]?.id ?? active);
  const [draftUnits, setDraftUnits] = useState<SearchUnit[]>(() =>
    draftFor(units, activeUnitId),
  );
  const [draftActiveUnitId, setDraftActiveUnitId] = useState<string>(() =>
    activeIn(draftUnits, activeUnitId),
  );

  // Booking code — staged like dates/guests and applied on the unified search
  // commit. The draft lives in the store rather than in local state: this bar
  // and the mobile sheet are alternate renderers of one field, and crossing the
  // breakpoint unmounts whichever is showing. Deep-link `?code=` and codes
  // applied in the sheet arrive through the same field, so no re-seeding.
  const draftBookingCode = useBookingCodeStore((state) => state.draft);
  const setDraftBookingCode = useBookingCodeStore((state) => state.setDraft);
  const commitBookingCode = useBookingCodeCommit(wbeConfig?.siteId ?? "");

  // Tracks whether either popover is currently open. The previous version
  // gated on HeaderContext's calendar flag, but the popover open/close state
  // actually lives in <BookingSearch />'s local `activePill` — they could
  // drift apart, letting the sync effect overwrite in-progress drafts. The
  // open/close callbacks below keep this flag in lockstep with the real
  // popover state.
  const [isEditing, setIsEditing] = useState(false);

  // Re-seed drafts whenever the COMMITTED context changes while the popover
  // is closed (search commit, mobile-sheet commit, URL restore). Deliberately
  // NOT keyed on `isEditing`: closing the popover must not fire this — staged
  // edits survive close-without-commit. The ref (not the state) gates it so
  // the isEditing flip itself can't re-run the effect.
  const isEditingRef = useRef(isEditing);
  useEffect(() => {
    isEditingRef.current = isEditing;
  }, [isEditing]);
  useEffect(() => {
    if (isEditingRef.current) return;
    setDraftStartDate(selectedStartDate);
    setDraftEndDate(selectedEndDate);
    const next = draftFor(units, activeUnitId);
    setDraftUnits(next);
    setDraftActiveUnitId(activeIn(next, activeUnitId));
    // `activeIn` is a plain function of its arguments.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStartDate, selectedEndDate, units, activeUnitId, draftFor]);

  const draftActiveUnit =
    draftUnits.find((u) => u.id === draftActiveUnitId) ?? draftUnits[0];
  const draftActiveGuests = draftActiveUnit?.guests ?? [];

  const draftAdultCount = draftActiveGuests.filter(
    (g) => g.ageType === "adult",
  ).length;
  const draftTeenagerCount = draftActiveGuests.filter(
    (g) => g.ageType === "teenager",
  ).length;
  const draftChildGuests: Guest[] = draftActiveGuests
    .filter((g) => g.ageType === "child")
    .map((g) => ({
      age: g.age,
      bedCapacityType: g.bedCapacityType,
      ageType: g.ageType,
    }));
  const draftInfantGuests: Guest[] = draftActiveGuests
    .filter((g) => g.ageType === "infant")
    .map((g) => ({
      age: g.age,
      bedCapacityType: g.bedCapacityType,
      ageType: g.ageType,
    }));
  const draftChildCount = draftChildGuests.length;
  const draftInfantCount = draftInfantGuests.length;
  const draftActiveUnitTotal =
    draftAdultCount + draftTeenagerCount + draftChildCount + draftInfantCount;
  // Totals across every unit — the collapsed pill describes the whole party,
  // not just the active/last-expanded unit (see draftAdultCount etc. above,
  // which stay scoped to the active unit for the open accordion section).
  const draftGuestTypeTotals = useMemo(
    () => sumGuestTypesAcrossUnits(draftUnits),
    [draftUnits],
  );
  const draftTotalAcrossUnits = draftUnits.reduce(
    (sum, u) => sum + u.guests.length,
    0,
  );

  const guestLimits = {
    adult: {
      min: wbeConfig?.maxConfig?.minGuests?.adults ?? 0,
      max: wbeConfig?.maxConfig?.maxGuests.adults ?? 10,
    },
    teenager: {
      min: wbeConfig?.maxConfig?.minGuests?.teenagers ?? 0,
      max: wbeConfig?.maxConfig?.maxGuests.teenagers ?? 10,
    },
    child: {
      min: wbeConfig?.maxConfig?.minGuests?.children ?? 0,
      max: wbeConfig?.maxConfig?.maxGuests.children ?? 10,
    },
    infant: {
      min: wbeConfig?.maxConfig?.minGuests?.infants ?? 0,
      max: wbeConfig?.maxConfig?.maxGuests.infants ?? 10,
    },
  };
  const maxTotalGuests = wbeConfig?.maxConfig?.maxTotalGuests ?? 16;
  // An activity availability search prices ONE guest mix — multiple groups
  // have nothing to map onto, so the add-group control never shows.
  const maxUnits = isActivity ? 1 : wbeConfig?.maxConfig?.maxUnits;

  const setDraftGuestCount = (ageType: AgeType, count: number) => {
    if (!wbeConfig) return;
    setDraftUnits((prev) =>
      prev.map((unit) => {
        if (unit.id !== draftActiveUnitId) return unit;
        return {
          ...unit,
          guests: resizeGuestList(unit.guests, ageType, count, wbeConfig),
        };
      }),
    );
  };

  const updateDraftGuestByActiveIndex = (
    index: number,
    updates: Partial<Guest>,
  ) => {
    setDraftUnits((prev) =>
      prev.map((unit) => {
        if (unit.id !== draftActiveUnitId) return unit;
        if (index < 0 || index >= unit.guests.length) return unit;
        const guests = unit.guests.slice();
        guests[index] = { ...guests[index], ...updates } as Guest;
        return { ...unit, guests };
      }),
    );
  };

  const addDraftUnit = () => {
    if (typeof maxUnits === "number" && draftUnits.length >= maxUnits) return;
    const newId = createUnitId();
    setDraftUnits((prev) => {
      const active = prev.find((u) => u.id === draftActiveUnitId) ?? prev[0];
      const seededGuests = active
        ? active.guests.map((g) => ({ ...g }))
        : [{ ageType: "adult", bedCapacityType: "full" } as Guest];
      return [...prev, { id: newId, guests: seededGuests }];
    });
    setDraftActiveUnitId(newId);
  };

  const removeDraftUnit = (unitId: string) => {
    setDraftUnits((prev) => {
      if (prev.length <= 1) return prev;
      const next = prev.filter((u) => u.id !== unitId);
      if (!next.some((u) => u.id === draftActiveUnitId)) {
        setDraftActiveUnitId(next[0]!.id);
      }
      return next;
    });
  };

  // The age/bed-type editors target guests by their slot index within the
  // child/infant subset. Translate those subset indices to absolute indices
  // in the active unit's `guests` array.
  const resolveActiveGuestIndex = (ageType: AgeType, subsetIndex: number) => {
    let seen = 0;
    for (let i = 0; i < draftActiveGuests.length; i++) {
      if (draftActiveGuests[i]?.ageType === ageType) {
        if (seen === subsetIndex) return i;
        seen += 1;
      }
    }
    return -1;
  };

  const handleChildAgeChange = (childIndex: number, age: number) => {
    const idx = resolveActiveGuestIndex("child", childIndex);
    if (idx !== -1)
      updateDraftGuestByActiveIndex(idx, {
        age,
        bedCapacityType: getBedTypeForAge(age),
      });
  };
  const handleInfantAgeChange = (infantIndex: number, age: number) => {
    const idx = resolveActiveGuestIndex("infant", infantIndex);
    if (idx !== -1)
      updateDraftGuestByActiveIndex(idx, {
        age,
        bedCapacityType: getBedTypeForAge(age),
      });
  };

  const formatDateDisplay = (date: Date | null) => {
    if (!date) return t("header.select_date");
    return format(date, "d MMM", { locale: getDateFnsLocale() });
  };
  const formatGuestInfo = () => {
    if (!isActivity && draftUnits.length > 1) {
      return t("header.units_short", { count: draftUnits.length });
    }
    const parts: string[] = [];
    const totals = draftGuestTypeTotals;
    if (totals.adult > 0) parts.push(t("header.adult", { count: totals.adult }));
    if (totals.teenager > 0)
      parts.push(t("header.teenager", { count: totals.teenager }));
    if (totals.child > 0) parts.push(t("header.child", { count: totals.child }));
    if (totals.infant > 0)
      parts.push(t("header.infant", { count: totals.infant }));
    if (parts.length === 0)
      return isActivity ? t("header.select_participants") : t("header.select_guests");
    return parts.join(", ");
  };

  const labels: BookingSearchLabels = {
    // Activity searches bound days, they don't arrive/depart — reuse the
    // calendar hook's module-aware wording so both pills say the same thing.
    arrival: calendarLocalization.labels.arrival,
    departure: calendarLocalization.labels.departure,
    guests: isActivity ? t("header.participants") : t("header.guests"),
    selectDate: t("header.select_date"),
    defaultGuests: t("header.default_guests"),
    formatDate: formatDateDisplay,
    formatGuests: formatGuestInfo,
  };

  const stopTimeBlocked =
    !!draftStartDate &&
    !!draftEndDate &&
    isStayRangeBlockedByStopTime(
      draftStartDate,
      draftEndDate,
      bookingAvailability,
    );
  // Infants-only is never bookable — an infant cannot occupy a unit alone.
  const hasInfantsOnlyUnit = draftUnits.some((u) =>
    isInfantsOnlyGuestList(u.guests),
  );
  const isUpdateDisabled = draftTotalAcrossUnits === 0 || hasInfantsOnlyUnit;
  const searchDisabled =
    !draftStartDate || !draftEndDate || isUpdateDisabled || stopTimeBlocked;

  const commitAndSearch = async () => {
    if (isUpdateDisabled) return;
    if (unitCapReached(booked.size, wbeConfig?.maxConfig?.maxUnits)) {
      toasty({ title: t("booking.max_units_hint"), variant: "error" });
      return;
    }
    if (stopTimeBlocked) {
      const blockedMessage =
        getStopTimeMessageForStayRange(
          draftStartDate,
          draftEndDate,
          bookingAvailability,
          getLanguageCode(),
        ) ?? t("warning.stop_time_blocked_description");
      toasty({
        title: t("warning.stop_time_blocked_title"),
        description: blockedMessage,
        variant: "warning",
      });
      return;
    }

    // Reconcile the booking code into the store FIRST so the post-commit
    // useSearch read (which keys off the store's applied code) sees the right
    // value before the dates/guests flush + onSearch fire.
    //
    // Never let this stop the search. The hook PATCHes an existing cart, and a
    // rejection (expired reservation, 400, dropped connection) used to skip the
    // flush and onSearch below entirely — the guest pressed Search and nothing
    // happened at all, with only the mutation's toast to go on. The code failing
    // to commit means prices simply stay as they were, which the search can show.
    if (wbeConfig?.acceptBookingCodes) {
      try {
        await commitBookingCode(draftBookingCode);
      } catch {
        // Reported by the mutation's own error handler.
      }
    }

    // flushSync so useSearch.handleSearch (called via onSearch → Landing)
    // reads the post-commit context state on the same tick — without it the
    // early `if (!selectedStartDate) return` there would see stale nulls.
    // The URL has to carry the committed mix too, not just React state. It holds ONE unit's
    // guests and `unit` says which, and the booking flow re-hydrates from it on entry — so a
    // URL still describing the guest count the page was loaded with gets stamped back onto the
    // active unit. Unit 1 set to three adults came back as one the moment the flow opened.
    // Serialized from the draft, since the flush below has not landed yet, and from unit 1 to
    // match the `unit=1` the flow starts on.
    const committedSearch = serializeSearchParams({
      base: window.location.search,
      dates:
        draftStartDate && draftEndDate
          ? { arrival: formatLocalDate(draftStartDate), departure: formatLocalDate(draftEndDate) }
          : null,
      guests: draftUnits[0]?.guests ?? [],
    });
    window.history.replaceState(
      {},
      "",
      committedSearch ? `${window.location.pathname}?${committedSearch}` : window.location.pathname,
    );

    flushSync(() => {
      setSelectedStartDate(draftStartDate);
      setSelectedEndDate(draftEndDate);
      // The booked units stay exactly as they are, in place; the draft replaces only the
      // units still to book.
      replaceUnits([...units.filter((u) => booked.has(u.id)), ...draftUnits]);
      // Unit 1, not whichever unit the guest last expanded: the URL written above describes
      // unit 1, and the flow starts there, so the header's own pointer has to agree.
      setActiveUnitId(draftUnits[0]?.id ?? draftActiveUnitId);
    });
    setIsEditing(false);
    setOpenCalendarRequest(false);
    // Commit first, search second. This used to return before any of the above when the guest
    // had not picked dates yet, so Update from the guests popover threw the whole edit away:
    // the popover showed the draft right up until it closed, and the header kept the units it
    // had. Setting unit 1 to three adults, adding unit 2 with one, and pressing Update left
    // unit 1 on one adult and no second unit at all. Guests are a valid thing to change on
    // their own; only the search itself needs a date range.
    if (!draftStartDate || !draftEndDate) return;
    onSearch?.();
  };

  const handleDatesChange = (from: Date | null, to: Date | null) => {
    // Allow tap-to-deselect the start date when no end is set yet.
    if (
      from &&
      draftStartDate &&
      isSameDay(from, draftStartDate) &&
      !draftEndDate &&
      !to
    ) {
      setDraftStartDate(null);
      setSelectedStartDate(null);
      setSelectedEndDate(null);
      return;
    }
    setDraftStartDate(from);
    setDraftEndDate(to);
    // Any incomplete range (either bound missing) flips the landing back to
    // the preSearch "all categories" view live — matches the pre-draft
    // behavior where isSearchActive turned off as soon as a date was cleared
    // or a new range was started before its end was picked.
    if (!from || !to) {
      setSelectedStartDate(null);
      setSelectedEndDate(null);
    }
  };

  const handlePopoversOpen = () => setIsEditing(true);

  // Just close — search only fires on explicit commit (search icon / Update
  // button). Drafts stay staged in the pill; they re-seed only when the
  // committed context changes.
  const handlePopoversClose = () => {
    setIsEditing(false);
    setOpenCalendarRequest(false);
  };

  return (
    <BookingSearch
      variant="app"
      selectedStartDate={draftStartDate}
      selectedEndDate={draftEndDate}
      currentMonth={currentMonth}
      onMonthChange={setCurrentMonth}
      onDatesChange={handleDatesChange}
      bookingAvailability={bookingAvailability}
      availabilityLoading={bookingAvailabilityFetching}
      guestSelectProps={{
        adults: draftAdultCount,
        teenagers: draftTeenagerCount,
        children: draftChildCount,
        infants: draftInfantCount,
        childGuests: draftChildGuests,
        infantGuests: draftInfantGuests,
        onAdultsChange: (count) => setDraftGuestCount("adult", count),
        onTeenagersChange: (count) => setDraftGuestCount("teenager", count),
        onChildrenChange: (count) => setDraftGuestCount("child", count),
        onInfantsChange: (count) => setDraftGuestCount("infant", count),
        onChildAgeChange: handleChildAgeChange,
        onInfantAgeChange: handleInfantAgeChange,
        guestLimits,
        totalGuests: draftActiveUnitTotal,
        maxTotalGuests,
        guestTypesConfig: wbeConfig?.guestTypesConfig,
        labels: guestSelectLabels,
      }}
      calendarLabels={calendarLocalization.labels}
      languageCode={calendarLocalization.languageCode}
      labels={labels}
      singleDaySelect={isActivity}
      searchDisabled={!!searchDisabled}
      onSearch={commitAndSearch}
      onPopoversClose={handlePopoversClose}
      onPopoversOpen={handlePopoversOpen}
      openPillRequest={openCalendarRequest ? "dates" : null}
      onOpenPillRequestHandled={() => setOpenCalendarRequest(false)}
      afterGuests={
        wbeConfig?.acceptBookingCodes
          ? ({ open, otherActive, onOpenChange }) => (
              <BookingCodeSearchSegment
                siteId={wbeConfig?.siteId ?? ""}
                value={draftBookingCode}
                onChange={setDraftBookingCode}
                open={open}
                otherActive={otherActive}
                onOpenChange={onOpenChange}
              />
            )
          : undefined
      }
      renderGuestsPopover={(defaultGuestSelect, closePopovers) => (
        <MultiUnitGuestsCard
          units={draftUnits}
          activeUnitId={draftActiveUnitId}
          setActiveUnitId={setDraftActiveUnitId}
          guestSelect={defaultGuestSelect}
          onAddUnit={addDraftUnit}
          onRemoveUnit={removeDraftUnit}
          onUpdate={() => {
            closePopovers();
            commitAndSearch();
          }}
          // The cap counts the booked units too; the card only holds the ones still to book.
          // No search button of its own: the bar's commits the draft with the dates.
          maxUnits={
            typeof maxUnits === "number"
              ? Math.max(1, maxUnits - booked.size)
              : maxUnits
          }
          hideUpdate
          isUpdateDisabled={isUpdateDisabled}
          updateDisabledLabel={t("error.error-at-least-one-guest")}
          t={t}
        />
      )}
    />
  );
};

const MultiUnitGuestsCard: React.FC<{
  units: SearchUnit[];
  activeUnitId: string;
  setActiveUnitId: (id: string) => void;
  guestSelect: React.ReactNode;
  onAddUnit: () => void;
  onRemoveUnit: (id: string) => void;
  onUpdate: () => void;
  maxUnits: number | undefined;
  isUpdateDisabled: boolean;
  updateDisabledLabel: string;
  /** No search button of its own: the bar's is the one way to search. The draft is staged
   *  like the dates and committed by the same press. */
  hideUpdate?: boolean;
  t: ReturnType<typeof useTranslation>["t"];
}> = ({
  units,
  activeUnitId,
  setActiveUnitId,
  guestSelect,
  onAddUnit,
  onRemoveUnit,
  onUpdate,
  maxUnits,
  isUpdateDisabled,
  hideUpdate = false,
  updateDisabledLabel,
  t,
}) => {
  // Activity sites book groups, not lodging units — same model, other words.
  const { isActivity: isActivitySite } = useAdFormat();
  const reduceMotion = useReducedMotion();

  // "Lägg till boende" collapses whatever section is open and expands the
  // new unit in the same click — two height changes plus a growing list, all
  // in one frame. No easing makes that read as deliberate, so this one
  // transition is instant instead. The flag flips on, forced into its own
  // commit via flushSync so the still-open unit's motion.div picks up
  // duration:0 one render BEFORE it exits (AnimatePresence animates an
  // exiting element using its last-rendered props, not the render where it
  // disappears) — then the add itself runs, and a render later the flag
  // resets so the next header click animates normally again.
  const [isInstantUnitChange, setIsInstantUnitChange] = useState(false);
  useEffect(() => {
    if (isInstantUnitChange) setIsInstantUnitChange(false);
  }, [isInstantUnitChange]);
  const handleAddUnitInstant = () => {
    flushSync(() => setIsInstantUnitChange(true));
    onAddUnit();
  };
  const skipTransition = reduceMotion || isInstantUnitChange;

  return (
    <div className="flex max-h-[calc(100dvh-8rem)] w-[360px] max-w-[90vw] flex-col rounded-3xl border border-border bg-surface-card shadow-card">
      <div className="min-h-0 flex-1 divide-y divide-border overflow-y-auto">
        {maxUnits === 1 ? (
          // One group only (activity sites): no group accordion header.
          <div className="px-0 pt-2">
            {guestSelect}
          </div>
        ) : (
        units.map((unit, idx) => {
          const isActive = unit.id === activeUnitId;
          const body = (
            <div className="px-0">
              {guestSelect}
              {units.length > 1 && (
                <div className="px-4 pb-2">
                  <Button
                    variant="secondary"
                    size="small"
                    className="!h-8 !rounded-full"
                    onClick={() => onRemoveUnit(activeUnitId)}
                  >
                    {t("booking.remove")}
                  </Button>
                </div>
              )}
            </div>
          );
          return (
            <div key={unit.id}>
              <button
                type="button"
                className="flex w-full items-center justify-between px-5 py-4 text-left"
                onClick={() =>
                  setActiveUnitId(unit.id === activeUnitId ? "" : unit.id)
                }
              >
                <span className="text-sm font-medium text-ink-secondary">
                  {t(isActivitySite ? "booking.group" : "booking.unit", { number: idx + 1 })}
                </span>
                <FiChevronRight
                  className={`h-5 w-5 transition-transform duration-200 ${isActive ? "rotate-90" : ""}`}
                />
              </button>
              <AnimatePresence initial={false}>
                {isActive && (
                  <motion.div
                    key="body"
                    className="overflow-hidden"
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: skipTransition ? 0 : 0.25, ease: "easeOut" }}
                  >
                    {body}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          );
        })
        )}
      </div>
      {(maxUnits !== 1 || !hideUpdate) && (
        <div className="flex shrink-0 items-center justify-between gap-3 border-t border-border p-4">
          {maxUnits !== 1 && (
            <Button
              variant="secondary"
              size="large"
              // Alone in the footer (no search button beside it) it takes the row.
              className={`!h-11 !rounded-full ${hideUpdate ? "w-full" : ""}`}
              onClick={handleAddUnitInstant}
              disabled={typeof maxUnits === "number" && units.length >= maxUnits}
            >
              <FiPlus className="mr-2 h-4 w-4" />
              {t(isActivitySite ? "booking.add_group" : "booking.add_unit")}
            </Button>
          )}
          {!hideUpdate && (
            <Tooltip label={updateDisabledLabel} disabled={!isUpdateDisabled}>
              {/* span wrapper so the tooltip still receives hover events when the
                  inner Button is disabled (browsers swallow pointer events on a
                  disabled button itself). */}
              <span className="inline-block">
                <Button
                  variant="secondary"
                  size="large"
                  className="!h-11 !rounded-full !bg-primary !text-on-primary hover:!bg-primary/90"
                  onClick={onUpdate}
                  disabled={isUpdateDisabled}
                >
                  {t("booking.update")}
                </Button>
              </span>
            </Tooltip>
          )}
        </div>
      )}
      {/* Only where there is an add button to explain: a one-group card has no cap to hit. */}
      {maxUnits !== 1 && typeof maxUnits === "number" && units.length >= maxUnits && (
        <div className="shrink-0 px-5 pb-4 text-xs font-medium text-accent">
          {t("booking.max_units_hint")}
        </div>
      )}
    </div>
  );
};
